import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Sparkles, Zap, Crown,
  Phone, ShoppingCart, AlertCircle, Check, Video, 
  X, ChevronRight, TrendingUp, Shirt, Film, Package,
  Copy, CheckCircle, Send, Mail, MessageCircle
} from 'lucide-react';
import { API_URL } from '../../config';
import StripePaymentForm from './StripePaymentForm';

// Particules animées de fond
const AnimatedBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-float"></div>
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-float-delayed"></div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
    {[...Array(30)].map((_, i) => (
      <div
        key={i}
        className="absolute w-1 h-1 bg-purple-400/30 rounded-full animate-float"
        style={{
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          animationDelay: `${i * 0.3}s`,
          animationDuration: `${3 + Math.random() * 4}s`
        }}
      />
    ))}
  </div>
);

// Configuration des crédits par service
const CREDITS_CONFIG = {
  video_ads: {
    id: 'video_ads',
    name: 'TeckiA Pub Vidéos',
    icon: Video,
    gradient: 'from-red-600 via-orange-600 to-yellow-600',
    description: 'Vidéos publicitaires IA',
    credits: [
      {
        type: 'videos',
        name: 'Crédits Vidéos',
        icon: '🎬',
        price: 15000,
        quota: 5,
        unit: 'vidéos',
        description: 'Vidéos générées par IA',
        badge: 'Populaire'
      }
    ]
  },
  // 'teckia_vtryon' retire temporairement de la boutique credits (module masque du dashboard,
  // meme raison : necessite encore une page active, migration incomplete).
  marketing_studio: {
    id: 'marketing_studio',
    name: 'Marketing Studio AI',
    icon: Film,
    gradient: 'from-purple-600 via-pink-600 to-cyan-600',
    description: 'Création d\'images et vidéos marketing',
    credits: [
      {
        type: 'images',
        name: 'Crédits Images Marketing',
        icon: '📸',
        price: 6000,
        quota: 10,
        unit: 'images',
        description: 'Images marketing IA — 600 Ar / image',
        badge: 'Nouveau'
      },
      {
        type: 'videos',
        name: 'Crédits Vidéos marketing',
        icon: '🎬',
        price: 18500,
        quota: 10,
        unit: 'vidéos',
        description: 'Vidéos marketing premium'
      }
    ]
  }
};

export default function CreditsPurchase({ onBack, initialService = null }) {
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [selectedService, setSelectedService] = useState(initialService);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  
  // Paiement
  const [paymentMethod, setPaymentMethod] = useState('mvola');
  const [showStripeForm, setShowStripeForm] = useState(false);

  // États MVola Manuel
  const [mvolaRef, setMvolaRef] = useState('');
  const [mvolaClientPhone, setMvolaClientPhone] = useState('');
  const [mvolaStep, setMvolaStep] = useState(1);
  const [mvolaCopied, setMvolaCopied] = useState(false);
  const [mvolaSubmitting, setMvolaSubmitting] = useState(false);
  const [mvolaSuccess, setMvolaSuccess] = useState(false);

  const MVOLA_NUMBER = '+261 34 26 126 52';
  const MVOLA_NUMBER_CLEAN = '0342612652';
  const MVOLA_NAME = 'TDI DIGITAL MG';
  const CONTACT_EMAIL = 'contact@tdidigital.io';

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/subscriptions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const activeSubs = (data.subscriptions || []).filter(sub => 
          sub.status === 'active' && ['video_ads', 'teckia_vtryon', 'marketing_studio'].includes(sub.service_id)
        );
        setActiveSubscriptions(activeSubs);
      }
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasActiveSubscription = (serviceId) => {
    return activeSubscriptions.some(sub => sub.service_id === serviceId);
  };

  const getAvailableServices = () => {
    // Modèle 100% crédits : tous les services sont achetables, sans abonnement préalable
    return Object.values(CREDITS_CONFIG);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const getTotalAmount = () => {
    return cart.reduce((sum, item) => sum + item.totalPrice, 0);
  };

  const copyMvolaNumber = () => {
    navigator.clipboard.writeText(MVOLA_NUMBER_CLEAN);
    setMvolaCopied(true);
    setTimeout(() => setMvolaCopied(false), 2500);
  };

  const submitMvolaManual = async () => {
    const refClean = mvolaRef.trim();
    const phoneClean = mvolaClientPhone.replace(/\s/g, '');

    if (!refClean || refClean.length < 4) {
      alert('❌ Veuillez entrer la référence de votre transfert MVola');
      return;
    }
    if (!phoneClean || phoneClean.length < 9) {
      alert('❌ Veuillez entrer votre numéro de téléphone');
      return;
    }

    setMvolaSubmitting(true);

    try {
      const totalAmount = getTotalAmount();
      const userId = localStorage.getItem('user_id');

      const payload = {
        to: CONTACT_EMAIL,
        subject: `💳 [MVola Crédits] Paiement à vérifier - ${refClean}`,
        user_id: userId,
        mvola_ref: refClean,
        client_phone: phoneClean,
        amount: totalAmount,
        cart: cart,
        purchase_type: 'credits'
      };

      await fetch(`${API_URL}/api/mvola/manual-confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      setMvolaSuccess(true);
    } catch (error) {
      console.error('Erreur:', error);
      setMvolaSuccess(true);
    } finally {
      setMvolaSubmitting(false);
    }
  };

  const processPayment = async () => {
    if (cart.length === 0) {
      alert('❌ Votre panier est vide');
      return;
    }

    if (paymentMethod === 'orange_money') {
      await processOrangeMoneyPayment();
      return;
    }

    if (paymentMethod === 'stripe') {
      setShowStripeForm(true);
      return;
    }

    // MVola: passer à l'étape 2
    if (paymentMethod === 'mvola') {
      setMvolaStep(2);
    }
  };

  const processOrangeMoneyPayment = async () => {
    setPurchasing(true);

    try {
      const firstItem = cart[0];
      
      const payload = {
        user_id: localStorage.getItem('user_id'),
        service_id: firstItem.service,
        amount: getTotalAmount(),
        plan: 'credits',
        duration: 0,
        purchase_type: 'credits',
        items: JSON.stringify(cart)
      };

      const response = await fetch(`${API_URL}/api/orange-money/initiate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success && result.payment_url) {
        localStorage.setItem('pending_orange_order', result.order_id);
        localStorage.setItem('pending_orange_amount', getTotalAmount());
        window.location.href = result.payment_url;
      } else {
        alert('❌ Erreur: ' + (result.error || 'Erreur inconnue'));
        setPurchasing(false);
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      alert('❌ Erreur réseau');
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-white text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  const availableServices = getAvailableServices();

  if (availableServices.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
        <AnimatedBackground />
        
        <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-40">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img src="/TeckiA-logo.gif" alt="TeckiA" className="w-12 h-12 rounded-full" />
                <div>
                  <h1 className="text-xl font-bold text-white">Acheter des Crédits</h1>
                  <p className="text-sm text-blue-200">Rechargez vos quotas</p>
                </div>
              </div>
              
              <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all">
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
            </div>
          </div>
        </header>

        <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-100px)] p-8">
          <div className="max-w-2xl w-full text-center">
            <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-full flex items-center justify-center border-4 border-yellow-500/30">
              <AlertCircle className="w-16 h-16 text-yellow-400" />
            </div>
            
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                Aucun Abonnement Actif
              </span>
            </h2>
            
            <p className="text-xl text-white/70 mb-8 max-w-xl mx-auto">
              Vous devez avoir un abonnement actif pour acheter des crédits supplémentaires.
            </p>
            
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">Pourquoi s'abonner ?</h3>
              <div className="grid gap-4 text-left">
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-white font-bold">Quotas mensuels inclus</div>
                    <div className="text-sm text-white/60">Crédits automatiques chaque mois</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-white font-bold">Achats de crédits supplémentaires</div>
                    <div className="text-sm text-white/60">Achetez à tout moment si besoin</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-white font-bold">Support prioritaire 24/7</div>
                    <div className="text-sm text-white/60">Assistance rapide et dédiée</div>
                  </div>
                </div>
              </div>
            </div>
            
            <button
              onClick={onBack}
              className="px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 hover:from-purple-700 hover:via-pink-700 hover:to-cyan-700 text-white font-bold text-lg transition-all hover:scale-110 shadow-2xl flex items-center gap-3 mx-auto"
            >
              <Crown className="w-6 h-6" />
              Voir les Abonnements
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      <AnimatedBackground />
      
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/TeckiA-logo.gif" alt="TeckiA" className="w-10 h-10 md:w-12 md:h-12 rounded-full" />
              <div>
                <h1 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-400 animate-pulse" />
                  Acheter des Crédits
                </h1>
                <p className="text-xs md:text-sm text-blue-200">
                  Rechargez vos quotas à tout moment
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {cart.length > 0 && (
                <div className="relative px-3 py-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    {cart.length}
                  </span>
                </div>
              )}
              
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Retour</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 md:py-12 relative z-10 max-w-7xl">
        
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 mb-6">
            <TrendingUp className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-300 font-bold text-sm">Boostez vos quotas</span>
          </div>
          
          <h2 className="text-4xl md:text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
              Achetez des Crédits
            </span>
          </h2>
          <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto">
            Rechargez instantanément vos quotas et continuez à créer sans limites
          </p>
        </div>

        {/* Sélection du service si pas initial */}
        {!selectedService && (
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Choisissez votre service</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {availableServices.map(service => (
                <ServiceSelector
                  key={service.id}
                  service={service}
                  onSelect={() => setSelectedService(service.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Configuration des crédits */}
        {selectedService && (
          <div className="mb-12">
            <CreditConfigurator
              service={CREDITS_CONFIG[selectedService]}
              cart={cart}
              setCart={setCart}
              onBack={() => setSelectedService(null)}
            />
          </div>
        )}

        {/* Panier */}
        {cart.length > 0 && (
          <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-purple-500/30 shadow-2xl">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
              <ShoppingCart className="w-7 h-7 text-purple-400" />
              Votre Panier ({cart.length})
            </h2>

            <div className="space-y-4 mb-6">
              {cart.map((item, index) => (
                <CartItemDisplay key={index} item={item} onRemove={() => removeFromCart(index)} />
              ))}
            </div>

            <div className="bg-white/5 rounded-2xl p-6 mb-6 border border-white/10">
              <div className="flex justify-between items-center mb-4">
                <span className="text-white font-bold text-2xl">TOTAL:</span>
                <span className="text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text font-bold text-3xl">
                  {getTotalAmount().toLocaleString()} Ar
                </span>
              </div>
            </div>

            {/* MÉTHODES DE PAIEMENT */}
            <div className="mb-6">
              <h3 className="text-white font-bold mb-4 text-lg">Méthode de paiement</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { id: 'mvola', name: 'MVola', img: 'mvola.png' },
                  { id: 'orange_money', name: 'Orange Money', img: 'orange.png' },
                  { id: 'stripe', name: 'Carte Bancaire', img: 'card.png' }
                ].map(method => (
                  <button
                    key={method.id}
                    onClick={() => {
                      setPaymentMethod(method.id);
                      setMvolaStep(1);
                      setMvolaSuccess(false);
                    }}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === method.id
                        ? 'border-purple-500 bg-purple-500/20 scale-105'
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                    }`}
                  >
                    <img src={`/${method.img}`} alt={method.name} className="w-16 h-16 mx-auto mb-2 object-contain" />
                    <div className="text-white font-medium">{method.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* MVOLA MANUEL */}
            {paymentMethod === 'mvola' && !mvolaSuccess && (
              <div className="mb-6">
                {mvolaStep === 1 && (
                  <MvolaInstructions
                    totalAmount={getTotalAmount()}
                    mvolaNumber={MVOLA_NUMBER}
                    mvolaNumberClean={MVOLA_NUMBER_CLEAN}
                    mvolaName={MVOLA_NAME}
                    mvolaCopied={mvolaCopied}
                    onCopy={copyMvolaNumber}
                    onContinue={() => setMvolaStep(2)}
                  />
                )}

                {mvolaStep === 2 && (
                  <MvolaConfirmForm
                    mvolaRef={mvolaRef}
                    setMvolaRef={setMvolaRef}
                    mvolaClientPhone={mvolaClientPhone}
                    setMvolaClientPhone={setMvolaClientPhone}
                    totalAmount={getTotalAmount()}
                    mvolaNumber={MVOLA_NUMBER}
                    mvolaName={MVOLA_NAME}
                    onBack={() => setMvolaStep(1)}
                    onSubmit={submitMvolaManual}
                    submitting={mvolaSubmitting}
                  />
                )}
              </div>
            )}

            {/* SUCCÈS MVola */}
            {paymentMethod === 'mvola' && mvolaSuccess && (
              <MvolaSuccessView
                mvolaRef={mvolaRef}
                onDone={() => {
                  setCart([]);
                  setMvolaRef('');
                  setMvolaClientPhone('');
                  setMvolaStep(1);
                  setMvolaSuccess(false);
                  onBack();
                }}
              />
            )}

            {/* ORANGE MONEY */}
            {paymentMethod === 'orange_money' && (
              <div className="mb-6 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <img src="/orange.png" alt="Orange Money" className="w-12 h-12 object-contain" />
                  <div>
                    <h4 className="text-white font-bold text-lg">Paiement Orange Money</h4>
                    <p className="text-orange-300 text-sm">Simple, rapide et sécurisé</p>
                  </div>
                </div>
              </div>
            )}

            {/* STRIPE */}
            {paymentMethod === 'stripe' && showStripeForm && (
              <div className="mb-6">
                <StripePaymentForm
                  cart={cart}
                  getTotalAmount={getTotalAmount}
                  onSuccess={() => {
                    alert('✅ Paiement réussi ! Crédits ajoutés.');
                    setCart([]);
                    setShowStripeForm(false);
                    onBack();
                  }}
                  onCancel={() => setShowStripeForm(false)}
                />
              </div>
            )}

            {/* BOUTON PAYER */}
            {!(paymentMethod === 'mvola' && (mvolaStep === 2 || mvolaSuccess)) && (
              <button
                onClick={processPayment}
                disabled={purchasing}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 hover:from-purple-700 hover:via-pink-700 hover:to-cyan-700 text-white font-bold text-lg transition-all hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {purchasing ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Traitement...
                  </span>
                ) : (
                  <>
                    {paymentMethod === 'orange_money' ? (
                      <>
                        <img src="/orange.png" alt="Orange Money" className="w-6 h-6" />
                        Payer avec Orange Money • {getTotalAmount().toLocaleString()} Ar
                      </>
                    ) : paymentMethod === 'mvola' ? (
                      <>📱 Continuer avec MVola • {getTotalAmount().toLocaleString()} Ar</>
                    ) : (
                      <>💳 Payer {getTotalAmount().toLocaleString()} Ar</>
                    )}
                  </>
                )}
              </button>
            )}

            <p className="text-center text-sm text-white/40 mt-4">
              🔒 Paiement sécurisé • Crédits ajoutés instantanément
            </p>
          </div>
        )}

        {cart.length === 0 && selectedService && (
          <div className="text-center py-12">
            <Package className="w-20 h-20 mx-auto mb-4 text-white/20" />
            <p className="text-white/60 text-lg">Configurez vos crédits ci-dessus</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPOSANTS MVOLA MANUEL (IDENTIQUES À SUBSCRIPTIONPAGE)
// ═══════════════════════════════════════════════════════════════

function MvolaInstructions({ totalAmount, mvolaNumber, mvolaNumberClean, mvolaName, mvolaCopied, onCopy, onContinue }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-yellow-500/30 shadow-2xl">
      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 px-6 py-5 border-b border-yellow-500/20">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
            <img src="/mvola.png" alt="MVola" className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <div>
            <h4 className="text-white font-bold text-lg">Paiement MVola</h4>
            <p className="text-yellow-300/80 text-sm">Transfert direct vers notre compte</p>
          </div>
          <div className="ml-auto text-right">
            <div className="text-white/60 text-sm">À payer</div>
            <div className="text-2xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text">
              {totalAmount.toLocaleString()} Ar
            </div>
          </div>
        </div>
      </div>

      <div className="bg-black/30 px-6 py-6 space-y-4">
        <div className="flex gap-4">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center font-bold text-black text-sm shadow-md">1</div>
          <div className="flex-1">
            <p className="text-white font-semibold mb-3">Effectuez le transfert MVola vers notre numéro :</p>
            <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/40 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-3xl font-bold text-white tracking-widest mb-1">{mvolaNumber}</div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-yellow-300 font-semibold text-sm">{mvolaName}</span>
                  </div>
                </div>
                <button onClick={onCopy} className={`flex flex-col items-center gap-1 px-4 py-3 rounded-xl border-2 transition-all font-bold text-sm ${mvolaCopied ? 'border-green-500 bg-green-500/20 text-green-400 scale-95' : 'border-yellow-500/50 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20 hover:scale-105 active:scale-95'}`}>
                  {mvolaCopied ? <><CheckCircle className="w-5 h-5" /><span>Copié !</span></> : <><Copy className="w-5 h-5" /><span>Copier</span></>}
                </button>
              </div>
              <div className="mt-3 pt-3 border-t border-yellow-500/20 flex items-center justify-between">
                <span className="text-white/60 text-sm">Montant exact à envoyer :</span>
                <span className="text-yellow-300 font-bold text-lg">{totalAmount.toLocaleString()} Ar</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center font-bold text-black text-sm shadow-md">2</div>
          <div className="flex-1">
            <p className="text-white font-semibold mb-1">Notez la référence du transfert</p>
            <p className="text-white/60 text-sm">Après le transfert, vous recevrez un SMS de confirmation MVola avec un numéro de référence. Conservez-le.</p>
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center font-bold text-black text-sm shadow-md">3</div>
          <div className="flex-1">
            <p className="text-white font-semibold mb-1">Soumettez la référence ici</p>
            <p className="text-white/60 text-sm">Cliquez sur "J'ai effectué le paiement" pour saisir votre référence. Notre équipe vérifiera et activera votre service sous <span className="text-green-400 font-semibold">24h</span>.</p>
          </div>
        </div>

        <div className="flex items-start gap-3 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-blue-200 text-sm">Vérifiez bien que le montant et le destinataire sont corrects avant de confirmer le transfert dans votre application MVola.</p>
        </div>
      </div>

      <div className="bg-black/20 px-6 py-5">
        <button onClick={onContinue} className="w-full py-4 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-bold text-lg transition-all hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2">
          <CheckCircle className="w-5 h-5" />
          J'ai effectué le paiement →
        </button>
        <p className="text-center text-white/40 text-xs mt-3">Cliquez seulement après avoir réalisé le transfert MVola</p>
      </div>
    </div>
  );
}

function MvolaConfirmForm({ mvolaRef, setMvolaRef, mvolaClientPhone, setMvolaClientPhone, totalAmount, mvolaNumber, mvolaName, onBack, onSubmit, submitting }) {
  const formatPhone = (value) => {
    let v = value.replace(/\D/g, '');
    if (v.length > 10) v = v.slice(0, 10);
    if (v.length >= 3) v = v.slice(0, 3) + ' ' + v.slice(3);
    if (v.length >= 7) v = v.slice(0, 6) + ' ' + v.slice(6);
    if (v.length >= 10) v = v.slice(0, 9) + ' ' + v.slice(9);
    return v;
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-yellow-500/30 shadow-2xl">
      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 px-6 py-5 border-b border-yellow-500/20">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h4 className="text-white font-bold text-lg">Confirmation du paiement</h4>
            <p className="text-yellow-300/80 text-sm">Entrez la référence de votre transfert</p>
          </div>
          <div className="ml-auto">
            <div className="text-2xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text">{totalAmount.toLocaleString()} Ar</div>
          </div>
        </div>
      </div>

      <div className="bg-black/30 px-6 py-6 space-y-5">
        <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <div className="text-white/60 text-xs">Paiement envoyé vers</div>
            <div className="text-white font-bold">{mvolaNumber} — <span className="text-green-400">{mvolaName}</span></div>
          </div>
        </div>

        <div>
          <label className="block text-white font-bold mb-2 flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center text-yellow-400 text-xs font-bold">1</span>
            Référence du transfert MVola *
          </label>
          <input type="text" placeholder="ex: MG2024123456789..." value={mvolaRef} onChange={(e) => setMvolaRef(e.target.value.toUpperCase())} className="w-full px-4 py-4 bg-white/10 border-2 border-yellow-500/30 rounded-xl text-white font-mono text-lg focus:outline-none focus:border-yellow-400 placeholder-white/30 transition-all" />
          <p className="text-white/50 text-xs mt-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Numéro de référence reçu par SMS après votre transfert MVola
          </p>
        </div>

        <div>
          <label className="block text-white font-bold mb-2 flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center text-yellow-400 text-xs font-bold">2</span>
            Votre numéro (contact / WhatsApp) *
          </label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input type="tel" placeholder="034 XX XX XXX" value={mvolaClientPhone} onChange={(e) => setMvolaClientPhone(formatPhone(e.target.value))} className="w-full pl-12 pr-4 py-4 bg-white/10 border-2 border-yellow-500/30 rounded-xl text-white text-lg focus:outline-none focus:border-yellow-400 placeholder-white/30 transition-all" maxLength={13} />
          </div>
          <p className="text-white/50 text-xs mt-2 flex items-center gap-1">
            <MessageCircle className="w-3 h-3 text-green-400" />
            Nous vous enverrons la confirmation d'activation par WhatsApp ou appel
          </p>
        </div>

        <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-1">Traitement sous 24h</p>
              <p className="text-white/60 text-xs leading-relaxed">Votre demande sera transmise à notre équipe qui vérifiera le paiement et ajoutera vos crédits. Vous serez contacté(e) dès confirmation.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-black/20 px-6 py-5 flex gap-3">
        <button onClick={onBack} className="flex-1 py-4 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold transition-all">Retour</button>
        <button onClick={onSubmit} disabled={submitting || !mvolaRef.trim() || !mvolaClientPhone.trim()} className="flex-2 w-full py-4 px-6 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-lg transition-all hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2" style={{ flex: 2 }}>
          {submitting ? (
            <>
              <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Envoi en cours...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Soumettre ma référence
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function MvolaSuccessView({ mvolaRef, onDone }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-green-500/40 shadow-2xl">
      <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 px-6 py-8 text-center border-b border-green-500/20">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-4 shadow-xl shadow-green-500/30">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Référence reçue !</h3>
        <p className="text-green-300/80 text-sm">Votre demande a été transmise à notre équipe</p>
      </div>

      <div className="bg-black/30 px-6 py-6 space-y-4">
        <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
          <div className="text-white/50 text-xs mb-1">Référence enregistrée</div>
          <div className="text-yellow-300 font-mono font-bold text-xl tracking-wider">{mvolaRef}</div>
        </div>

        <div className="space-y-3">
          {[
            { icon: '🔍', title: 'Vérification', desc: 'Notre équipe vérifie votre paiement MVola', color: 'text-blue-300' },
            { icon: '⚡', title: 'Activation', desc: 'Vos crédits sont ajoutés', color: 'text-purple-300' },
            { icon: '📲', title: 'Confirmation', desc: 'Vous êtes contacté(e) par WhatsApp ou appel', color: 'text-green-300' }
          ].map((step, i) => (
            <div key={i} className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="text-2xl">{step.icon}</div>
              <div className="flex-1">
                <div className={`font-bold text-sm ${step.color}`}>{step.title}</div>
                <div className="text-white/60 text-xs">{step.desc}</div>
              </div>
              <div className="w-2 h-2 rounded-full bg-white/20" />
            </div>
          ))}
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <p className="text-white/70 text-sm">Délai de traitement : <span className="text-yellow-300 font-bold">moins de 24h</span> en jours ouvrables</p>
        </div>
      </div>

      <div className="bg-black/20 px-6 py-5">
        <button onClick={onDone} className="w-full py-4 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold text-lg transition-all hover:scale-105 shadow-lg">
          Retour au Dashboard
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// AUTRES COMPOSANTS
// ═══════════════════════════════════════════════════════════════

function ServiceSelector({ service, onSelect }) {
  const Icon = service.icon;
  
  return (
    <button
      onClick={onSelect}
      className="group relative overflow-hidden rounded-3xl p-6 cursor-pointer transition-all duration-500 hover:scale-105 bg-gradient-to-br from-white/5 to-white/10 border-2 border-white/10 hover:border-purple-500/50"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br ${service.gradient} blur-2xl`}></div>
      
      <div className="relative z-10">
        <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-all duration-500`}>
          <Icon className="w-10 h-10 text-white" />
        </div>
        
        <h3 className="text-xl font-bold text-white mb-2 text-center">{service.name}</h3>
        <p className="text-sm text-white/60 text-center mb-4">{service.description}</p>
        
        <div className="flex items-center justify-center gap-2 text-purple-300 font-bold">
          <span>Sélectionner</span>
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </button>
  );
}

function CreditConfigurator({ service, cart, setCart, onBack }) {
  const [quantities, setQuantities] = useState({});
  const [videoProviders, setVideoProviders] = useState({});

  const updateQuantity = (creditType, value) => {
    setQuantities({ ...quantities, [creditType]: Math.max(1, parseInt(value) || 1) });
  };

  const updateVideoProvider = (creditType, provider) => {
    setVideoProviders({ ...videoProviders, [creditType]: provider });
  };

  const addCredit = (creditData) => {
    if (!creditData) {
      console.error('❌ Credit data is undefined');
      return;
    }

    const quantity = quantities[creditData.type] || 1;
    const videoProvider = videoProviders[creditData.type] || 'hailuo';
    
    const item = {
      service: service.id,
      serviceName: service.name,
      creditType: creditData.type,
      creditName: creditData.name,
      icon: creditData.icon,
      price: creditData.price,
      quantity: quantity,
      totalPrice: creditData.price * quantity,
      quota: creditData.quota,
      unit: creditData.unit,
      videoProvider: creditData.providers ? videoProvider : null
    };
    
    setCart([...cart, item]);
    alert(`✅ ${quantity} crédit(s) ${creditData.name} ajouté(s) au panier !`);
  };

  return (
    <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-white/10">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center`}>
            <service.icon className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white">{service.name}</h3>
            <p className="text-sm text-white/60">{service.description}</p>
          </div>
        </div>
        
        <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-all">
          <X className="w-6 h-6 text-white" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {service.credits && service.credits.map(creditItem => (
          <CreditCard
            key={creditItem.type}
            creditData={creditItem}
            quantity={quantities[creditItem.type] || 1}
            onQuantityChange={(val) => updateQuantity(creditItem.type, val)}
            videoProvider={videoProviders[creditItem.type] || 'hailuo'}
            setVideoProvider={(provider) => updateVideoProvider(creditItem.type, provider)}
            onAdd={() => addCredit(creditItem)}
          />
        ))}
      </div>
    </div>
  );
}

function CreditCard({ creditData, quantity, onQuantityChange, videoProvider, setVideoProvider, onAdd }) {
  if (!creditData) {
    console.error('❌ CreditCard: creditData is undefined');
    return null;
  }

  const totalPrice = creditData.price * quantity;
  const totalQuota = creditData.quota * quantity;

  return (
    <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-6 border border-white/20 hover:border-purple-500/50 transition-all">
      {creditData.badge && (
        <div className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 text-yellow-300 text-xs font-bold mb-4">
          ⭐ {creditData.badge}
        </div>
      )}
      
      <div className="flex items-center gap-3 mb-4">
        <div className="text-4xl">{creditData.icon}</div>
        <div>
          <h4 className="text-lg font-bold text-white">{creditData.name}</h4>
          <p className="text-xs text-white/60">{creditData.description}</p>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-4 mb-4 border border-white/10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-white/70">Prix unitaire:</span>
          <span className="text-white font-bold">{creditData.price.toLocaleString()} Ar</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-white/70">Par crédit:</span>
          <span className="text-purple-300 font-bold">{creditData.quota} {creditData.unit}</span>
        </div>
      </div>

      {creditData.providers && (
        <div className="mb-4">
          <label className="block text-sm text-white/70 mb-2">Provider vidéo:</label>
          <div className="grid grid-cols-2 gap-2">
            {creditData.providers.map(provider => (
              <button
                key={provider}
                onClick={() => setVideoProvider(provider)}
                className={`px-4 py-2 rounded-lg font-bold transition-all ${
                  videoProvider === provider ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {provider === 'hailuo' ? '🎥 Hailuo' : '🎬 WAN'}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4">
        <label className="block text-sm text-white/70 mb-2">Quantité:</label>
        <div className="flex items-center gap-2">
          <button onClick={() => onQuantityChange(quantity - 1)} className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold text-xl flex items-center justify-center transition-all">−</button>
          <input type="number" min="1" value={quantity} onChange={(e) => onQuantityChange(e.target.value)} className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-center font-bold text-xl focus:outline-none focus:border-purple-400" />
          <button onClick={() => onQuantityChange(quantity + 1)} className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold text-xl flex items-center justify-center transition-all">+</button>
        </div>
      </div>

      <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 mb-4 border border-purple-500/30">
        <div className="text-center mb-2">
          <div className="text-2xl font-bold text-white">{totalPrice.toLocaleString()} Ar</div>
          <div className="text-sm text-purple-300">= {totalQuota} {creditData.unit}</div>
        </div>
      </div>

      <button onClick={onAdd} className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold transition-all hover:scale-105 flex items-center justify-center gap-2">
        <ShoppingCart className="w-5 h-5" />
        Ajouter au panier
      </button>
    </div>
  );
}

function CartItemDisplay({ item, onRemove }) {
  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="text-3xl">{item.icon}</div>
            <div>
              <div className="text-white font-bold">{item.serviceName}</div>
              <div className="text-sm text-white/60">{item.creditName}</div>
            </div>
          </div>
          
          <div className="text-sm text-white/70 ml-12">
            <div>• {item.quantity} crédit(s) × {item.price.toLocaleString()} Ar</div>
            <div className="text-purple-300 font-bold">• = {item.quantity * item.quota} {item.unit}</div>
            {item.videoProvider && <div>• Provider: {item.videoProvider === 'hailuo' ? 'Hailuo' : 'WAN'}</div>}
          </div>
        </div>

        <div className="text-right">
          <div className="text-white font-bold text-lg mb-2">{item.totalPrice.toLocaleString()} Ar</div>
          <button onClick={onRemove} className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}